import {
  type FunctionComponent,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  useRef,
  useState,
} from 'react';

import VideoTimelineMarker from 'assets/images/video-timeline-marker.svg';

import S from './style.scss';

const STEP = 0.02;

type PropsT = {
  /** Seek to an absolute [0,1] fraction of the whole session. */
  onSeek: (fraction: number) => void;

  /** Current session progress, a [0,1] fraction. */
  progress: number;

  /** Chapter-boundary positions ([0,1] fractions) to mark on the bar. */
  ticks: number[];
};

/** A draggable/keyboard-seekable progress bar with chapter-boundary ticks. */
const ScrubBar: FunctionComponent<PropsT> = ({ onSeek, progress, ticks }) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState<null | number>(null);

  const fractionFromX = (clientX: number): number => {
    const el = trackRef.current;
    if (!el) return 0;
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0) return 0;
    return Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
  };

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>): void => {
    e.currentTarget.setPointerCapture(e.pointerId);
    setDrag(fractionFromX(e.clientX));
  };

  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>): void => {
    if (drag === null) return;
    setDrag(fractionFromX(e.clientX));
  };

  const onPointerUp = (e: ReactPointerEvent<HTMLDivElement>): void => {
    if (drag === null) return;
    const fraction = fractionFromX(e.clientX);
    setDrag(null);
    onSeek(fraction);
  };

  const onPointerCancel = (): void => {
    setDrag(null);
  };

  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'ArrowLeft') onSeek(Math.max(0, progress - STEP));
    else if (e.key === 'ArrowRight') onSeek(Math.min(1, progress + STEP));
  };

  const shown = drag ?? progress;

  return (
    <div
      aria-label="Seek"
      aria-valuemax={100}
      aria-valuemin={0}
      aria-valuenow={Math.round(shown * 100)}
      className={S.hitArea}
      onKeyDown={onKeyDown}
      onPointerCancel={onPointerCancel}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      role="slider"
      tabIndex={0}
    >
      <div className={S.track} ref={trackRef}>
        <div className={S.fill} style={{ width: `${shown * 100}%` }} />
        {ticks.map((t) => (
          <div
            className={S.tick}
            key={t}
            style={{ left: `${t * 100}%` }}
          />
        ))}
        <VideoTimelineMarker
          className={S.handle}
          style={{ left: `${shown * 100}%` }}
        />
      </div>
    </div>
  );
};

export default ScrubBar;
